import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Search,
  ExternalLink,
  Printer,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  Copy,
  Check,
  Layers,
  Database,
  Receipt,
  PiggyBank,
  GraduationCap,
  CreditCard,
  Radio,
  BarChart3,
  Wrench,
  Smartphone,
  HelpCircle,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

export const DocumentationView: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<'ALL' | 'KASIR' | 'KEUANGAN' | 'KEPSEK' | 'SUPERADMIN'>('ALL');
  const [activeTab, setActiveTab] = useState<string>('RINGKASAN');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const openFullscreenDocs = () => {
    window.open('/docs/index.html', '_blank');
  };

  const chapters = [
    { id: 'RINGKASAN', label: 'Ringkasan & Akun', icon: Layers, badge: 'Mulai' },
    { id: 'MASTER_DATA', label: 'Master Data & VA', icon: Database, badge: 'Dasar' },
    { id: 'TAGIHAN_SPP', label: 'Tagihan & SPP Berkala', icon: Layers, badge: 'Auto' },
    { id: 'LOKET_POS', label: 'Loket Pembayaran POS', icon: Receipt, badge: 'Utama' },
    { id: 'TABUNGAN_WALLET', label: 'Tabungan & E-Wallet', icon: PiggyBank, badge: 'Kantin' },
    { id: 'BEASISWA', label: 'Beasiswa & Approval', icon: GraduationCap, badge: 'Kepsek' },
    { id: 'GATEWAY', label: 'Payment Gateway (Duitku)', icon: CreditCard, badge: 'Online' },
    { id: 'RFID_PRESENSI', label: 'Presensi RFID (X107)', icon: Radio, badge: 'Sync' },
    { id: 'LAPORAN_AKUNTANSI', label: 'Laporan & Jurnal G/L', icon: BarChart3, badge: 'Keuangan' },
    { id: 'UTILITY_MAINTENANCE', label: 'Utility & Backup', icon: Wrench, badge: 'Admin' },
    { id: 'PORTAL_MOBILE', label: 'Portal Wali & Mobile', icon: Smartphone, badge: 'Wali' },
    { id: 'FAQ_TROUBLESHOOT', label: 'Solusi Masalah & FAQ', icon: HelpCircle, badge: 'Bantuan' },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Top Banner & Header */}
      <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-teal-900 text-white p-6 rounded-2xl shadow-lg border border-sky-700/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold text-amber-300 border border-white/15">
            <Sparkles className="w-3.5 h-3.5" />
            Dokumentasi Standar Penggunaan Aplikasi v2.5
          </div>
          <h1 className="text-xl sm:text-2xl font-black tracking-tight">
            Pusat Panduan & Dokumentasi Resmi SPBSDMT
          </h1>
          <p className="text-xs sm:text-sm text-sky-100 max-w-2xl leading-relaxed">
            Petunjuk lengkap alur operasional loket pembayaran, tagihan SPP berkala, verifikasi beasiswa, sinkronisasi RFID, gateway Duitku & Midtrans, dan pelaporan akuntansi SDMT Ponorogo.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={openFullscreenDocs}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-400 hover:bg-amber-300 text-sky-950 font-bold text-xs rounded-xl shadow-md transition cursor-pointer"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Buka Web Portal Docs</span>
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white font-semibold text-xs rounded-xl border border-white/20 transition cursor-pointer"
          >
            <Printer className="w-4 h-4" />
            <span>Cetak / PDF</span>
          </button>
        </div>
      </div>

      {/* Filter and Quick Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Search */}
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari kata kunci (contoh: void, spp, va, rfid)..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white transition"
          />
        </div>

        {/* Role Quick Selector */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0 text-xs">
          <span className="text-[11px] font-bold text-slate-400 mr-1 shrink-0">Peran:</span>
          <button
            onClick={() => { setSelectedRole('ALL'); }}
            className={`px-2.5 py-1 rounded-lg font-bold transition text-xs shrink-0 ${
              selectedRole === 'ALL' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Semua
          </button>
          <button
            onClick={() => { setSelectedRole('KASIR'); setActiveTab('LOKET_POS'); }}
            className={`px-2.5 py-1 rounded-lg font-semibold transition text-xs shrink-0 ${
              selectedRole === 'KASIR' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Kasir Loket
          </button>
          <button
            onClick={() => { setSelectedRole('KEUANGAN'); setActiveTab('TAGIHAN_SPP'); }}
            className={`px-2.5 py-1 rounded-lg font-semibold transition text-xs shrink-0 ${
              selectedRole === 'KEUANGAN' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Admin Keuangan
          </button>
          <button
            onClick={() => { setSelectedRole('KEPSEK'); setActiveTab('BEASISWA'); }}
            className={`px-2.5 py-1 rounded-lg font-semibold transition text-xs shrink-0 ${
              selectedRole === 'KEPSEK' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Kepala Sekolah
          </button>
          <button
            onClick={() => { setSelectedRole('SUPERADMIN'); setActiveTab('UTILITY_MAINTENANCE'); }}
            className={`px-2.5 py-1 rounded-lg font-semibold transition text-xs shrink-0 ${
              selectedRole === 'SUPERADMIN' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Superadmin
          </button>
        </div>
      </div>

      {/* Main Chapter Tabs & Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Left Column: Chapter List */}
        <div className="lg:col-span-1 space-y-1 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm max-h-[750px] overflow-y-auto">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider px-2 py-1 mb-1">
            Daftar Bab Panduan
          </p>
          {chapters.map((ch) => {
            const Icon = ch.icon;
            const isActive = activeTab === ch.id;
            return (
              <button
                key={ch.id}
                onClick={() => setActiveTab(ch.id)}
                className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left text-xs transition cursor-pointer ${
                  isActive
                    ? 'bg-sky-600 text-white font-bold shadow-sm'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-amber-300' : 'text-slate-500'}`} />
                  <span className="truncate">{ch.label}</span>
                </div>
                <span
                  className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {ch.badge}
                </span>
              </button>
            );
          })}

          {/* Quick link to standalone portal */}
          <div className="pt-3 mt-3 border-t border-slate-100 px-2">
            <button
              onClick={openFullscreenDocs}
              className="w-full py-2 px-3 bg-sky-50 hover:bg-sky-100 text-sky-800 font-bold text-xs rounded-xl flex items-center justify-between transition cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 text-sky-600" />
                <span>Dokumentasi HTML</span>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-sky-600" />
            </button>
          </div>
        </div>

        {/* Right Column: Active Content Details */}
        <div className="lg:col-span-3 space-y-6">

          {/* CHAPTER 1: RINGKASAN */}
          {activeTab === 'RINGKASAN' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 1</span>
                  <h2 className="text-lg font-bold text-slate-900">Gambaran Umum & Kredensial Login Default</h2>
                </div>
                <span className="text-xs bg-emerald-50 text-emerald-700 font-bold px-2 py-1 rounded-lg border border-emerald-200">
                  Siap Pakai
                </span>
              </div>

              {/* Architecture Intro */}
              <p className="text-xs text-slate-600 leading-relaxed">
                Aplikasi <strong>SPBSDMT (Sistem Pembayaran Siswa SDMT Ponorogo)</strong> merupakan aplikasi terintegrasi berbasis web SPA (React 19) dan RESTful API (Node.js/Express) dengan basis data MySQL. Sistem ini mengelola seluruh siklus keuangan siswa mulai dari tagihan rutin (SPP bulanan), pos insidental (seragam, buku, gedung), simpanan tabungan siswa, pembayaran online Duitku/Midtrans, hingga pencatatan jurnal akuntansi otomatis.
              </p>

              {/* Default Accounts Card */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <span className="text-xs font-bold text-slate-900 block">Daftar Akun Pengguna Standar Sistem:</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <span className="font-bold text-sky-900">👑 Superadmin / IT</span>
                    <p className="text-slate-500 text-[11px]">User: <code className="font-mono font-bold text-slate-800">admin</code> | Pass: <code className="font-mono font-bold text-slate-800">admin123</code></p>
                    <p className="text-[10px] text-slate-400">Akses penuh ke seluruh menu, user privilege, dan backup.</p>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <span className="font-bold text-emerald-900">🏪 Kasir Loket</span>
                    <p className="text-slate-500 text-[11px]">User: <code className="font-mono font-bold text-slate-800">kasir</code> | Pass: <code className="font-mono font-bold text-slate-800">kasir123</code></p>
                    <p className="text-[10px] text-slate-400">Pelunasan loket POS, kuitansi struk, setor/tarik tabungan.</p>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <span className="font-bold text-blue-900">💼 Admin Keuangan</span>
                    <p className="text-slate-500 text-[11px]">User: <code className="font-mono font-bold text-slate-800">keuangan</code> | Pass: <code className="font-mono font-bold text-slate-800">keuangan123</code></p>
                    <p className="text-[10px] text-slate-400">Generate SPP berkala, kelola pos tagihan, laporan, dan jurnal.</p>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <span className="font-bold text-amber-900">🎓 Kepala Sekolah</span>
                    <p className="text-slate-500 text-[11px]">User: <code className="font-mono font-bold text-slate-800">kepsek</code> | Pass: <code className="font-mono font-bold text-slate-800">kepsek123</code></p>
                    <p className="text-[10px] text-slate-400">Monitoring rekapitulasi dan verifikasi persetujuan beasiswa.</p>
                  </div>
                </div>
              </div>

              {/* Fast Jump Button */}
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-slate-500">Lanjutkan ke bab berikutnya:</span>
                <button
                  onClick={() => setActiveTab('MASTER_DATA')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs rounded-lg transition"
                >
                  <span>Bab 2: Master Data</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* CHAPTER 2: MASTER DATA */}
          {activeTab === 'MASTER_DATA' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 2</span>
                  <h2 className="text-lg font-bold text-slate-900">Master Data Sekolah & Virtual Account</h2>
                </div>
                <button
                  onClick={() => navigate('/siswa')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Data Siswa</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <div className="p-4 bg-sky-50/70 rounded-xl border border-sky-100 space-y-2">
                  <span className="font-bold text-sky-900 text-sm block">1. Otomasi Virtual Account (Bank Muamalat & Duitku):</span>
                  <p>
                    Setiap data siswa yang dimasukkan secara otomatis memiliki nomor Virtual Account dengan formula: <code className="font-mono font-bold text-sky-800 bg-white px-1.5 py-0.5 rounded border border-sky-200">899865 + NIS</code>.
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Contoh: Siswa dengan NIS <strong>002448</strong> akan memiliki VA: <code className="font-mono font-bold text-slate-800">8998650000002448</code>. Nomor ini tidak akan berubah dan dapat digunakan untuk pembayaran di ATM, Mobile Banking, atau Internet Banking seluruh bank di Indonesia.
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <span className="font-bold text-slate-900 text-sm block">2. Bagan Akun Keuangan (COA):</span>
                  <p>
                    Sistem menggunakan 5 kategori utama Chart of Accounts: <strong>Aset (1)</strong>, <strong>Kewajiban (2)</strong>, <strong>Ekuitas (3)</strong>, <strong>Pendapatan (4)</strong>, dan <strong>Biaya (5)</strong>.
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Setiap Pos Tagihan wajib dipetakan ke <em>Akun Penerimaan</em> dan <em>Akun Piutang</em> agar saat pembayaran lunas, jurnal debit/kredit langsung terbentuk otomatis.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 3: TAGIHAN & SPP */}
          {activeTab === 'TAGIHAN_SPP' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 3</span>
                  <h2 className="text-lg font-bold text-slate-900">Tagihan Siswa & Generator SPP Berkala</h2>
                </div>
                <button
                  onClick={() => navigate('/tagihan-spp-berkala')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Generator SPP</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Sistem mendukung 2 mode pembuatan tagihan:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <span className="font-bold text-slate-900">A. Tagihan Pos Insidental / Manual</span>
                    <p className="text-slate-500 text-[11px]">
                      Digunakan untuk tagihan insidental seperti Uang Gedung, Seragam Baru, atau Buku Tematik. Dibuat di menu <strong>Tagihan Siswa → Buat Tagihan</strong>.
                    </p>
                  </div>

                  <div className="p-3 bg-sky-50 rounded-xl border border-sky-200 space-y-1">
                    <span className="font-bold text-sky-900">B. Generator SPP Berkala (Batch Auto)</span>
                    <p className="text-slate-600 text-[11px]">
                      Menghasilkan tagihan SPP untuk seluruh siswa selama 12 bulan (Juli s.d Juni) hanya dalam satu klik dengan menerapkan potongan beasiswa yang disetujui secara otomatis.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-xl text-amber-900 text-xs">
                  <strong>Catatan Penting:</strong> Sistem memiliki proteksi anti-duplikasi. Jika tagihan untuk bulan dan pos yang sama sudah pernah dibuat, sistem tidak akan membuat tagihan ganda.
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 4: LOKET POS */}
          {activeTab === 'LOKET_POS' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Bab 4</span>
                  <h2 className="text-lg font-bold text-slate-900">Loket Pembayaran Kasir (Point of Sale) & Void</h2>
                </div>
                <button
                  onClick={() => navigate('/loket')}
                  className="text-xs bg-emerald-600 text-white font-bold px-3 py-1 rounded-lg hover:bg-emerald-700 transition flex items-center gap-1 shadow-sm"
                >
                  <span>Buka Loket Pembayaran</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <span className="font-bold text-slate-900 text-sm block">Alur Kerja Harian Petugas Kasir:</span>
                
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">1</span>
                    <p><strong>Cari Siswa:</strong> Ketik NIS, Nama Siswa, atau scan barcode pada kartu pelajar siswa.</p>
                  </div>

                  <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">2</span>
                    <p><strong>Centang Tagihan:</strong> Pilih tagihan yang akan dilunasi. Jumlah total otomatis terkalkulasi secara instan.</p>
                  </div>

                  <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">3</span>
                    <p><strong>Pilih Cara Bayar:</strong> Pilih Tunai, QRIS, atau Debet Saldo Tabungan Siswa.</p>
                  </div>

                  <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">4</span>
                    <p><strong>Cetak Kuitansi:</strong> Kuitansi kasir langsung tercetak dengan nomor resmi <code>KW-SDMT-...</code> dan kode verifikasi QR.</p>
                  </div>
                </div>

                <div className="p-3 bg-rose-50 rounded-xl border border-rose-200 text-rose-900 space-y-1">
                  <span className="font-bold block">Pembatalan Transaksi (Void):</span>
                  <p className="text-[11px]">
                    Jika terjadi kesalahan input, Superadmin dapat membuka menu <strong>Transaksi → Cetak Bukti & Void</strong>, klik tombol <em>Void</em>, dan memasukkan alasan. Status tagihan otomatis kembali menjadi UNPAID.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 5: TABUNGAN */}
          {activeTab === 'TABUNGAN_WALLET' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 5</span>
                  <h2 className="text-lg font-bold text-slate-900">Tabungan Siswa & Command Center E-Wallet</h2>
                </div>
                <button
                  onClick={() => navigate('/tabungan/mutasi')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Mutasi Tabungan</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Setiap siswa memiliki buku rekening tabungan digital. Petugas kasir dapat mencatat <strong>Setor Tunai</strong>, <strong>Tarik Tunai</strong>, atau memotong saldo tabungan untuk pembayaran tagihan sekolah.
                </p>

                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-emerald-900 space-y-2">
                  <span className="font-bold text-sm block">E-Wallet Kantin Sekolah (NFC & Barcode):</span>
                  <p>
                    Melalui menu <strong>E-Wallet (Kantin)</strong>, petugas kantin dapat memproses pembelian makanan/minuman siswa hanya dengan menempelkan kartu siswa ke reader. Dilengkapi pembatasan limit belanja per hari demi menjaga kebiasaan jajan sehat anak.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 6: BEASISWA */}
          {activeTab === 'BEASISWA' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Bab 6</span>
                  <h2 className="text-lg font-bold text-slate-900">Manajemen Beasiswa & Verifikasi Kepala Sekolah</h2>
                </div>
                <button
                  onClick={() => navigate('/data-beasiswa')}
                  className="text-xs bg-amber-500 text-white font-bold px-3 py-1 rounded-lg hover:bg-amber-600 transition flex items-center gap-1 shadow-sm"
                >
                  <span>Buka Data Beasiswa</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Kategori beasiswa yang didukung di SDMT Ponorogo mencakup: <strong>Beasiswa Prestasi</strong>, <strong>Beasiswa Yatim (Subsidi 50%)</strong>, <strong>Anak Guru/Pegawai</strong>, dan <strong>Afirmasi Khusus</strong>.
                </p>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <span className="font-bold text-slate-900 block">Prosedur Approval Kepala Sekolah:</span>
                  <ol className="list-decimal list-inside space-y-1.5 pl-1">
                    <li>Kepala Sekolah login menggunakan akun kepsek.</li>
                    <li>Buka menu <strong>Data Awal → Data Beasiswa/Potongan</strong>.</li>
                    <li>Lihat tab <em>Menunggu Verifikasi</em>. Periksa nama siswa, riwayat kehadiran presensi RFID, dan pos tagihan yang diajukan.</li>
                    <li>Klik <strong>Setujui (Approve)</strong> atau <strong>Tolak</strong>. Dapat juga memilih beberapa usulan sekaligus untuk <em>Batch Approval</em>.</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 7: GATEWAY */}
          {activeTab === 'GATEWAY' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 7</span>
                  <h2 className="text-lg font-bold text-slate-900">Payment Gateway Duitku & Midtrans</h2>
                </div>
                <button
                  onClick={() => navigate('/payment-gateway')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Gateway Console</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Sistem mendukung integrasi otomatis ke payment gateway Duitku dan Midtrans. Saat wali murid melakukan transfer ke nomor Virtual Account atau scan QRIS, server gateway secara otomatis mengirim notifikasi webhook callback ke backend sekolah.
                </p>

                <div className="p-3 bg-slate-900 text-slate-100 rounded-xl font-mono text-[11px] space-y-1">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>URL Callback Gateway Server:</span>
                    <button
                      onClick={() => handleCopy('https://domainsekolah.sch.id/api/gateway/callback', 'gw')}
                      className="text-amber-400 hover:underline flex items-center gap-1 font-sans"
                    >
                      {copiedKey === 'gw' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedKey === 'gw' ? 'Tersalin' : 'Salin'}</span>
                    </button>
                  </div>
                  <div className="text-emerald-400">https://domainsekolah.sch.id/api/gateway/callback</div>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 8: RFID */}
          {activeTab === 'RFID_PRESENSI' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 8</span>
                  <h2 className="text-lg font-bold text-slate-900">Presensi RFID Mesin Solution X107</h2>
                </div>
                <button
                  onClick={() => navigate('/siswa')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Halaman Sinkronisasi</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Sistem berkomunikasi langsung dengan mesin pembaca kartu RFID siswa model <strong>Solution X107</strong> di gerbang sekolah melalui protokol TCP socket (port 4370) pada alamat IP: <code className="font-mono font-bold text-slate-800 bg-slate-100 px-1 py-0.5 rounded">192.168.1.201</code>.
                </p>
                <p>
                  Data tap presensi ini digunakan untuk memantau kedisiplinan siswa sekaligus menjadi indikator penting dalam evaluasi pemberian beasiswa berprestasi.
                </p>
              </div>
            </div>
          )}

          {/* CHAPTER 9: LAPORAN & JURNAL */}
          {activeTab === 'LAPORAN_AKUNTANSI' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 9</span>
                  <h2 className="text-lg font-bold text-slate-900">Laporan Keuangan & Jurnal Umum Akuntansi</h2>
                </div>
                <button
                  onClick={() => navigate('/report/pembayaran')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Laporan</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Tersedia 5 laporan inti yang dapat diekspor langsung ke format <strong>Excel</strong> dan <strong>PDF Siap Cetak</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1.5 pl-1">
                  <li><strong>Laporan Rekap Tagihan:</strong> Status bayar per rombel dan per pos tagihan.</li>
                  <li><strong>Laporan Pembayaran Siswa:</strong> Rekap harian/bulanan penerimaan per kasir loket.</li>
                  <li><strong>Laporan Tunggakan Siswa:</strong> Daftar siswa yang belum melunasi kewajiban.</li>
                  <li><strong>Laporan Penerimaan Kas/Bank:</strong> Arus kas masuk per metode pembayaran.</li>
                  <li><strong>Jurnal Umum (General Ledger):</strong> Catatan debit & kredit standar akuntansi berbasis COA.</li>
                </ul>
              </div>
            </div>
          )}

          {/* CHAPTER 10: UTILITY */}
          {activeTab === 'UTILITY_MAINTENANCE' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider">Bab 10</span>
                  <h2 className="text-lg font-bold text-slate-900">Utility, Kenaikan Kelas, Kelulusan & Backup</h2>
                </div>
                <button
                  onClick={() => navigate('/utility/backup')}
                  className="text-xs bg-purple-50 text-purple-700 font-bold px-3 py-1 rounded-lg border border-purple-200 hover:bg-purple-100 transition flex items-center gap-1"
                >
                  <span>Buka Backup DB</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Menu Utility disediakan khusus untuk <strong>Superadmin</strong> guna melakukan perawatan rutin akhir tahun ajaran:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <span className="font-bold text-slate-900">1. Naik Kelas</span>
                    <p className="text-[11px] text-slate-500">Mutasi masal siswa kelas 1 s.d 5 ke tingkat kelas berikutnya.</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <span className="font-bold text-slate-900">2. Kelulusan Siswa</span>
                    <p className="text-[11px] text-slate-500">Mengarsipkan data siswa kelas 6 yang telah menyelesaikan studi.</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <span className="font-bold text-slate-900">3. Backup Database</span>
                    <p className="text-[11px] text-slate-500">Membuat dump berkas .sql secara aman untuk arsip lembaga.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 11: PORTAL */}
          {activeTab === 'PORTAL_MOBILE' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 11</span>
                  <h2 className="text-lg font-bold text-slate-900">Portal Siswa/Wali Murid & Aplikasi Mobile</h2>
                </div>
                <button
                  onClick={() => navigate('/portal')}
                  className="text-xs bg-sky-50 text-sky-700 font-bold px-3 py-1 rounded-lg border border-sky-200 hover:bg-sky-100 transition flex items-center gap-1"
                >
                  <span>Buka Portal Siswa</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <p>
                  Wali murid dapat mengakses informasi keuangan anak melalui browser web di tautan <code>/portal</code> atau mengunduh aplikasi mobile di <code>/mobile</code>.
                </p>
                <div className="p-3 bg-sky-50 rounded-xl border border-sky-200 text-sky-900 space-y-1">
                  <span className="font-bold">Panduan Login Wali Murid:</span>
                  <p className="text-[11px]">Masukkan <strong>NIS Siswa</strong> dan <strong>PIN Default: 123456</strong>.</p>
                </div>
              </div>
            </div>
          )}

          {/* CHAPTER 12: FAQ */}
          {activeTab === 'FAQ_TROUBLESHOOT' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Bab 12</span>
                <h2 className="text-lg font-bold text-slate-900">Tanya Jawab & Penanganan Masalah (FAQ)</h2>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-900 block">Q1: Bagaimana jika salah input pembayaran di loket?</span>
                  <p className="text-slate-600 leading-relaxed">
                    Lakukan <strong>Void Transaksi</strong> di menu <em>Transaksi → Cetak Bukti & Void</em> dengan akun Superadmin. Tagihan akan kembali menjadi belum lunas dan siap diinput ulang secara benar.
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-900 block">Q2: Bagaimana jika wali murid lupa PIN portal mobile?</span>
                  <p className="text-slate-600 leading-relaxed">
                    Admin dapat membuka menu <em>Data Awal → Siswa & Virtual Account</em>, cari nama siswa, lalu klik tombol <strong>Reset PIN</strong> untuk mengembalikan PIN ke default (<code>123456</code>).
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                  <span className="font-bold text-slate-900 block">Q3: Di mana melihat laporan tunggakan siswa yang belum bayar?</span>
                  <p className="text-slate-600 leading-relaxed">
                    Buka menu <strong>Report → Laporan Tunggakan Siswa</strong>. Anda dapat memfilter per angkatan atau per rombel kelas, lalu klik <em>Export Excel</em> atau <em>Cetak PDF</em>.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};
